/**
 * CronTab.tsx — Settings → Cron: per-project cron management.
 *
 * The desired state (task jobs + hook settings) is edited here and saved to
 * the project manifest; the backend reconciles the VSKI platform's ``http``
 * cron jobs from it (see `backend/cron.py`) — those jobs call back into this
 * server's hooks. The task picker is the SAME poll the Code tab's Tasks
 * panel renders (`GET /api/projects/{id}/tasks`): any task the project has
 * — model, notebook, workflow worker, a trainer, a project model, the image
 * refresh — can be scheduled, with the panel's own Run/Stop/Restart
 * semantics. Workflow-schedule triggers are NOT managed here — they live in
 * the graph's trigger editor ("on schedule" fires), same reconcile. SQL cron
 * (data retention) stays managed on the platform itself.
 *
 * Rows: hook base URL (where the platform reaches this server), the hook
 * token (copy/rotate), the job list (arm/disarm, edit, delete, add), and a
 * Sync-now button with the platform-side view (drift feedback).
 */
import { useEffect, useState } from 'react';
import {
  ArrowClockwise, CalendarBlank, Check, Copy, Key, PencilSimple, Plus, Trash,
} from '@phosphor-icons/react';

import {
  type CronJob, type CronState, type CronSync, type TaskRow,
  cronScheduleError, getCron, listTasks, putCronJobs, putCronSettings,
  rotateCronToken, syncCron, taskRowKey,
} from '../projects/cronApi';
import { useConfirm } from './ConfirmDialog';

interface CronTabProps {
  /** The open backend project (empty = no backend project → hidden). */
  projectId: string;
  backendOrigin: string | null;
  token: string | null;
}

interface JobDraft {
  id: string;
  label: string;
  schedule: string;
  /** The picked task row's identity (kind, or kind:name for per-module). */
  taskKey: string;
  action: CronJob['action'];
  seed: string;
  enabled: boolean;
}

const EMPTY_DRAFT = (id: string): JobDraft => ({
  id, label: '', schedule: '', taskKey: '', action: 'start', seed: '', enabled: true,
});

const ACTION_LABELS: Record<CronJob['action'], string> = {
  start: 'Start',
  stop: 'Stop',
  restart: 'Restart',
};

const TASK_HINTS: Record<string, string> = {
  model: 'the model subprocess (run/start)',
  notebook: 'the Jupyter server',
  workflows: 'the workflow worker',
  trainer: 'a trainer (trainers/<name>.py)',
  mlmodel: 'a project model (models/<name>.py)',
  images: 'the image pull + rebuild (docker execution)',
  task: 'a manifest-declared custom task (project.json tasks)',
};

const SCHEDULE_PRESETS: { label: string; expr: string }[] = [
  { label: 'hourly', expr: '0 * * * *' },
  { label: 'daily 9:00', expr: '0 9 * * *' },
  { label: 'every 15m', expr: '*/15 * * * *' },
  { label: 'weekly Mon 9:00', expr: '0 9 * * 1' },
];

/** Kinds whose start accepts a reproducibility seed. */
const SEEDABLE = new Set(['model', 'trainer', 'mlmodel']);

function draftFromJob(job: CronJob): JobDraft {
  return {
    id: job.id,
    label: job.label ?? '',
    schedule: job.schedule,
    taskKey: job.target ? `${job.task}:${job.target}` : job.task,
    action: job.action,
    seed: job.seed != null ? String(job.seed) : '',
    enabled: job.enabled !== false,
  };
}

/** Split a task row key back into (kind, target). */
function splitTaskKey(key: string): { kind: string; target?: string } {
  const i = key.indexOf(':');
  if (i < 0) return { kind: key };
  return { kind: key.slice(0, i), target: key.slice(i + 1) };
}

function draftToJob(d: JobDraft): CronJob {
  const { kind, target } = splitTaskKey(d.taskKey);
  return {
    id: d.id,
    label: d.label.trim() || undefined,
    schedule: d.schedule.trim(),
    task: kind as CronJob['task'],
    target: target || undefined,
    action: d.action,
    seed: d.seed.trim() ? Number(d.seed) : undefined,
    enabled: d.enabled,
  };
}

function SyncSummary({ sync }: { sync: CronSync | null }): JSX.Element | null {
  if (!sync) return null;
  if (sync.error) {
    return <span className="trigger-status-error" title={sync.error}>platform: {sync.error}</span>;
  }
  if (sync.errors.length > 0) {
    return (
      <span className="trigger-status-error" title={sync.errors.join('\n')}>
        sync: {sync.errors.length} error(s)
      </span>
    );
  }
  const parts: string[] = [];
  if (sync.created) parts.push(`${sync.created} created`);
  if (sync.updated) parts.push(`${sync.updated} updated`);
  if (sync.deleted) parts.push(`${sync.deleted} deleted`);
  return <span className="param-hint">{parts.length > 0 ? `synced (${parts.join(', ')})` : 'in sync'}</span>;
}

export function CronTab({ projectId, backendOrigin, token }: CronTabProps): JSX.Element {
  const confirm = useConfirm();
  const [state, setState] = useState<CronState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mutError, setMutError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<CronSync | null>(null);
  const [editing, setEditing] = useState<'new' | number | null>(null);
  const [draft, setDraft] = useState<JobDraft>(() => EMPTY_DRAFT(`cron_${Date.now().toString(36)}`));
  const [taskRows, setTaskRows] = useState<TaskRow[] | null>(null);
  const [baseUrlInput, setBaseUrlInput] = useState('');
  const [tokenCopied, setTokenCopied] = useState(false);

  const reload = (): void => {
    if (!backendOrigin) return;
    getCron(backendOrigin, token, projectId)
      .then((s) => {
        setState(s);
        setLoadError(null);
        setBaseUrlInput(s.baseUrl ?? '');
      })
      .catch((e: Error) => setLoadError(e.message));
  };

  useEffect(reload, [backendOrigin, token, projectId]);

  // The task picker's options — the same rows the Code tab's Tasks panel
  // polls (that poll is the source of truth for "what tasks does this
  // project have"). Fetched once per tab open; re-open the tab to refresh
  // after the project's code changes.
  useEffect(() => {
    if (!backendOrigin) return;
    let alive = true;
    listTasks(backendOrigin, token, projectId)
      .then((rows) => { if (alive) setTaskRows(rows); })
      .catch(() => { if (alive) setTaskRows([]); });
    return () => { alive = false; };
  }, [backendOrigin, token, projectId]);

  const mutate = async (fn: () => Promise<{ sync?: CronSync }>): Promise<void> => {
    setBusy(true);
    setMutError(null);
    try {
      const r = await fn();
      if (r.sync) setLastSync(r.sync);
      reload();
    } catch (e) {
      setMutError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const saveJobs = (jobs: CronJob[]): Promise<void> =>
    mutate(() => putCronJobs(backendOrigin!, token, projectId, jobs));

  const saveBaseUrl = (): void => {
    const v = baseUrlInput.trim();
    void mutate(() => putCronSettings(backendOrigin!, token, projectId, v || null));
  };

  const rotate = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Rotate the hook token?',
      body: 'Existing schedules keep working (the platform jobs are updated to the new token immediately), but any external caller using the old token stops.',
      confirmLabel: 'Rotate',
    });
    if (!ok) return;
    void mutate(() => rotateCronToken(backendOrigin!, token, projectId));
  };

  const copyToken = async (): Promise<void> => {
    if (!state?.hookToken) return;
    try {
      await navigator.clipboard.writeText(state.hookToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 1500);
    } catch { /* clipboard unavailable — nothing to surface */
    }
  };

  const startAdd = (): void => {
    setDraft(EMPTY_DRAFT(`cron_${Date.now().toString(36)}`));
    setEditing('new');
    setMutError(null);
  };

  const startEdit = (i: number): void => {
    const job = jobs[i];
    if (!job) return;
    setDraft(draftFromJob(job));
    setEditing(i);
    setMutError(null);
  };

  const saveDraft = (): void => {
    const job = draftToJob(draft);
    const current = state?.jobs ?? [];
    const next = editing === 'new'
      ? [...current, job]
      : current.map((j, i) => (i === editing ? job : j));
    setEditing(null);
    void saveJobs(next);
  };

  const deleteJob = async (i: number): Promise<void> => {
    const job = state?.jobs[i];
    if (!job) return;
    const ok = await confirm({
      title: `Delete "${job.label || job.id}"?`,
      body: 'The platform cron job is deleted with it — the schedule stops firing.',
      danger: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    void saveJobs((state?.jobs ?? []).filter((_, j) => j !== i));
  };

  const syncNow = (): void => {
    setBusy(true);
    setMutError(null);
    syncCron(backendOrigin!, token, projectId)
      .then((r) => setLastSync(r.sync))
      .then(reload)
      .catch((e: Error) => setMutError(e.message))
      .finally(() => setBusy(false));
  };

  if (!backendOrigin) {
    return (
      <div className="settings-section">
        <p className="param-hint">Connect to a backend first.</p>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="settings-section">
        <p className="trigger-fire-error">{loadError}</p>
        <button type="button" className="btn" onClick={reload}>Retry</button>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="settings-section">
        <p className="param-hint">Loading cron…</p>
      </div>
    );
  }

  const scheduleError = editing !== null ? cronScheduleError(draft.schedule) : null;
  const effectiveBase = (baseUrlInput.trim() || state.defaultBaseUrl).replace(/\/+$/, '');
  const jobs = state.jobs;
  const picked = taskRows?.find((r) => taskRowKey(r) === draft.taskKey);
  const pickedKind = picked?.kind ?? splitTaskKey(draft.taskKey).kind;
  // The image refresh is start-only (a compose pull/build, not a managed
  // process) — same rule the Tasks panel's row applies to its buttons.
  const actionChoices: CronJob['action'][] = pickedKind === 'images'
    ? ['start']
    : ['start', 'stop', 'restart'];

  return (
    <div className="settings-section">
      {/* ── Hook settings ─────────────────────────────────────────────── */}
      <div className="settings-row">
        <div className="settings-row-label">
          <CalendarBlank size={16} aria-hidden />
          <div>
            <div className="settings-row-title">Hook base URL</div>
          </div>
        </div>
        <div className="cron-inline-edit">
          <input
            className="settings-input" type="text" value={baseUrlInput}
            placeholder={state.defaultBaseUrl} disabled={busy}
            onChange={(e) => setBaseUrlInput(e.target.value)}
          />
          <button type="button" className="btn" disabled={busy || baseUrlInput.trim() === (state.baseUrl ?? '')} onClick={saveBaseUrl}>
            Save
          </button>
        </div>
      </div>
      <div className="settings-row">
        <div className="settings-row-label">
          <Key size={16} aria-hidden />
          <div>
            <div className="settings-row-title">Hook token</div>
          </div>
        </div>
        {state.hookToken ? (
          <div className="cron-inline-edit">
            <button
              type="button" className="settings-copy-block" onClick={() => void copyToken()}
              title="Copy" aria-label="Copy hook token"
            >
              <code>{state.hookToken.slice(0, 10)}…</code>
              {tokenCopied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => void rotate()}>
              Rotate
            </button>
          </div>
        ) : (
          <span className="param-hint">minted when the first schedule is saved</span>
        )}
      </div>

      {/* ── Scheduled tasks ───────────────────────────────────────────── */}
      <div className="cron-jobs-head">
        <div className="settings-row-title">Scheduled tasks</div>
        <div className="cron-jobs-tools">
          <SyncSummary sync={lastSync ?? (state.platform.error ? { ...ZERO_SYNC, error: state.platform.error } : null)} />
          <button type="button" className="btn btn-mini" disabled={busy} onClick={syncNow}>
            <ArrowClockwise size={13} aria-hidden /> Sync now
          </button>
        </div>
      </div>
      {mutError && <p className="trigger-fire-error">{mutError}</p>}
      <div className="trigger-list">
        {jobs.length === 0 && editing === null && (
          <p className="agents-empty">
            No scheduled tasks — add one below. (Workflow schedules live in the
            graph's trigger editor.)
          </p>
        )}
        {jobs.map((job, i) => (
          <div key={job.id} className="trigger-row">
            <button
              type="button" className="code-task-btn"
              title={job.enabled === false ? 'Disarmed — click to arm' : 'Armed — click to disarm'}
              style={{ opacity: job.enabled === false ? 0.4 : 1 }}
              disabled={busy}
              onClick={() => void saveJobs(
                jobs.map((j, k) => (k === i ? { ...j, enabled: j.enabled === false } : j)),
              )}
            >
              <CalendarBlank size={14} />
            </button>
            <button
              type="button" className="trigger-row-main" title="Edit"
              onClick={() => startEdit(i)}
            >
              <div className="trigger-row-title">
                {job.label || job.id} — {ACTION_LABELS[job.action]} {job.target ?? job.task}
              </div>
              <div className="trigger-row-meta">
                <code>{job.schedule}</code>
                <span className="agents-mode">{job.task}</span>
                {job.enabled === false && <span className="agents-fence read-only">disarmed</span>}
                {job.seed != null && <span className="param-hint">seed {job.seed}</span>}
              </div>
            </button>
            {editing === i ? null : (
              <button type="button" className="code-task-btn" title="Edit" onClick={() => startEdit(i)}
                      disabled={editing !== null}>
                <PencilSimple size={14} />
              </button>
            )}
            <button
              type="button" className="code-task-btn danger" title="Delete" disabled={busy}
              onClick={() => void deleteJob(i)}
            >
              <Trash size={13} />
            </button>
          </div>
        ))}

        {editing !== null && (
          <div className="cron-job-edit">
            <div className="trigger-grid">
              <label className="settings-label">
                Label
                <input
                  className="settings-input" type="text" value={draft.label}
                  placeholder="Nightly model restart"
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                />
              </label>
              <label className="settings-label">
                Schedule (cron)
                <input
                  className="settings-input" type="text" value={draft.schedule}
                  placeholder="0 9 * * *"
                  onChange={(e) => setDraft({ ...draft, schedule: e.target.value })}
                />
              </label>
              <label className="settings-label">
                Task
                <select
                  className="settings-select" value={draft.taskKey}
                  onChange={(e) => {
                    const key = e.target.value;
                    const kind = splitTaskKey(key).kind;
                    // Keep the action legal for the newly picked task (the
                    // image refresh is start-only).
                    const action = kind === 'images' && draft.action !== 'start'
                      ? 'start' : draft.action;
                    setDraft({ ...draft, taskKey: key, action });
                  }}
                >
                  <option value="">
                    {taskRows === null ? 'loading tasks…' : taskRows.length === 0 ? '(no tasks)' : '(choose a task)'}
                  </option>
                  {(taskRows ?? []).map((r) => (
                    <option key={taskRowKey(r)} value={taskRowKey(r)}>
                      {r.label} · {r.kind}
                    </option>
                  ))}
                  {/* A saved task the poll no longer lists (code removed, or
                      the row is hidden in this deployment) still selects. */}
                  {draft.taskKey && !(taskRows ?? []).some((r) => taskRowKey(r) === draft.taskKey) && (
                    <option value={draft.taskKey}>{draft.taskKey}</option>
                  )}
                </select>
              </label>
              <label className="settings-label">
                Action
                <select
                  className="settings-select" value={draft.action} disabled={!draft.taskKey}
                  onChange={(e) => setDraft({ ...draft, action: e.target.value as CronJob['action'] })}
                >
                  {actionChoices.map((a) => (
                    <option key={a} value={a}>{ACTION_LABELS[a]}</option>
                  ))}
                </select>
              </label>
              {SEEDABLE.has(pickedKind) && (
                <label className="settings-label">
                  Seed (optional)
                  <input
                    className="settings-input" type="number" value={draft.seed}
                    onChange={(e) => setDraft({ ...draft, seed: e.target.value })}
                  />
                </label>
              )}
            </div>
            <div className="trigger-expr-row">
              <div className="cron-presets">
                {SCHEDULE_PRESETS.map((p) => (
                  <button
                    key={p.expr} type="button" className="btn btn-mini"
                    title={p.expr}
                    onClick={() => setDraft({ ...draft, schedule: p.expr })}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {draft.schedule && (
                <code className="trigger-expr-preview">
                  {effectiveBase}/api/hooks/{projectId}/cron/{draft.id}
                </code>
              )}
            </div>
            {scheduleError && draft.schedule
              ? <p className="trigger-fire-error">{scheduleError}</p>
              : <p className="param-hint">
                  5 fields: minute hour day month weekday (server local time).
                  {pickedKind && ` Task: ${TASK_HINTS[pickedKind] ?? pickedKind}.`}
                </p>}
            <div className="modal-footer-row">
              <label className="trigger-armed-toggle">
                <input
                  type="checkbox" checked={draft.enabled}
                  onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                />
                Armed
              </label>
              <span className="flex-spacer" />
              <button type="button" className="btn" onClick={() => setEditing(null)}>Cancel</button>
              <button
                type="button" className="btn btn-primary"
                disabled={!draft.taskKey || !draft.schedule.trim() || !!scheduleError}
                onClick={saveDraft}
              >
                Save schedule
              </button>
            </div>
          </div>
        )}

        {editing === null && (
          <button type="button" className="btn agents-add" disabled={busy} onClick={startAdd}>
            <Plus size={14} /> Add schedule
          </button>
        )}
      </div>

      {/* ── Platform view (drift feedback) ────────────────────────────── */}
      <div className="settings-row meta-row">
        <div className="settings-row-label">
          <CalendarBlank size={16} aria-hidden />
          <div>
            <div className="settings-row-title">Platform jobs</div>
            <div className="settings-row-hint">
              {state.platform.error
                ? `unreachable: ${state.platform.error}`
                : state.platform.jobs.length === 0
                  ? 'none on the platform yet — Sync now creates them'
                  : `${state.platform.jobs.length} job(s), including workflow schedules`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ZERO_SYNC: CronSync = { created: 0, updated: 0, deleted: 0, errors: [] };
